import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import { PublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";
import { getBaseUrl } from "./utils/urls";
import { getAzureAccessToken } from "./utils/azureTokenHelper";
import { getCurrentMsalAccount } from "./utils/sessionIdentity";
import { msalConfig, apiScopes, loginRequest } from "./config/azureEntraIdConfig";

export const PROD = "PRODUCTION";
export const QA = "QA";
export const DEV = "DEVELOPMENT";
export const LOCAL = "LOCAL";
export let ENV: string;

// Initialize MSAL instance for token acquisition
const msalInstance = new PublicClientApplication(msalConfig);
let msalInitialized = false;

// Initialize MSAL
msalInstance.initialize().then(() => {
  msalInitialized = true;
}).catch((error) => {
  console.error("Failed to initialize MSAL:", error);
});

axios.defaults.headers.post["Content-Type"] = "application/json";

const instance: AxiosInstance = axios.create({
  baseURL: getBaseUrl(),
  timeout: 30000,
});

instance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const scopes = apiScopes.length ? apiScopes : loginRequest.scopes;
    const acquireTokenWithRetry = async () => {
      try {
        const token = await getAzureAccessToken(msalInstance);
        if (token) return token;
      } catch (err) {
        // swallow and retry with forceRefresh below
        console.warn("Silent token acquisition failed, will retry with forceRefresh", err);
      }

      const account = getCurrentMsalAccount(msalInstance);
      if (!account) {
        console.warn("No MSAL account found; cannot acquire token");
        return null;
      }

      try {
        const tokenResponse = await msalInstance.acquireTokenSilent({
          scopes,
          forceRefresh: true,
          account,
        });
        return tokenResponse.accessToken;
      } catch (err) {
        if (!(err instanceof InteractionRequiredAuthError)) {
          console.error("Token forceRefresh failed:", err);
        }
        return null;
      }
    };

    // Wait for MSAL to initialize
    if (!msalInitialized) {
      await msalInstance.initialize();
      msalInitialized = true;
    }

    const token = await acquireTokenWithRetry();

    if (!token) {
      const reason = new axios.Cancel("No auth token available; request canceled");
      return Promise.reject(reason);
    }

    if (config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers["ngrok-skip-browser-warning"] = "any";
    }
    
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => { // NOSONAR
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle expired tokens by attempting a single silent refresh + retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const account = getCurrentMsalAccount(msalInstance);
        if (account) {
          const refreshed = await msalInstance.acquireTokenSilent({
            scopes: apiScopes.length ? apiScopes : loginRequest.scopes,
            forceRefresh: true,
            account,
          });
          if (refreshed?.accessToken && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${refreshed.accessToken}`;
            return instance(originalRequest);
          }
        } else {
          console.warn("No MSAL account available during 401 retry; skipping retry");
        }
      } catch (refreshErr) {
        console.error("Token refresh after 401 failed:", refreshErr);
      }
    }

    if (error.response?.data) {
      const errorData = error.response.data as { errorMessage?: string; message?: string };
      const errorMessage = errorData.errorMessage || errorData.message || "An error occurred";
      console.error("API Error:", errorMessage);
    }

    return Promise.reject(error);
  }
);

export default instance;
